import { useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';

import { EnterpriseSsoProvider, lookUpEnterpriseSsoProviderTypes } from '@neo.tax/shared';

/** Long enough that the lookup runs once someone pauses typing, rather than once per keystroke. */
const LOOKUP_DEBOUNCE_MS = 400;

/** How long a lookup may go unanswered before it counts as inconclusive, so a stalled request cannot hold the form. */
const LOOKUP_TIMEOUT_MS = 5000;

/**
 * What is known about the provider types configured for the ID now typed. Debouncing until typing pauses, then
 * checking while the lookup is under way; inconclusive once no answer will come, because the ID is not looked up or
 * the lookup failed or ran out of time.
 */
export type EnterpriseSsoProviderLookup =
  | { status: 'debouncing' }
  | { status: 'checking' }
  | { status: 'inconclusive' }
  | { status: 'answered'; configuredTypes: ReadonlySet<EnterpriseSsoProvider> };

// An email address is never a provider ID, so it is not sent to Identity Platform.
const isLookedUp = (providerIdSuffix: string): boolean => !!providerIdSuffix && !providerIdSuffix.includes('@');

/** Looks the provider ID up once typing pauses, rather than on every keystroke. */
export const useEnterpriseSsoProviderLookup = (providerIdSuffix: string): EnterpriseSsoProviderLookup => {
  const [debouncedProviderIdSuffix] = useDebounce(providerIdSuffix, LOOKUP_DEBOUNCE_MS);
  const [lookup, setLookup] = useState<{
    providerIdSuffix: string;
    configuredTypes: ReadonlySet<EnterpriseSsoProvider> | null;
  } | null>(null);

  useEffect(() => {
    if (!isLookedUp(debouncedProviderIdSuffix)) {
      return;
    }
    let isOpen = true;
    const settle = (configuredTypes: ReadonlySet<EnterpriseSsoProvider> | null): void => {
      if (isOpen) {
        isOpen = false;
        setLookup({ providerIdSuffix: debouncedProviderIdSuffix, configuredTypes });
      }
    };
    const timeoutId = setTimeout(() => settle(null), LOOKUP_TIMEOUT_MS);
    const lookUp = async (): Promise<void> => {
      try {
        settle(await lookUpEnterpriseSsoProviderTypes(debouncedProviderIdSuffix));
      } catch {
        settle(null);
      }
    };
    void lookUp();
    return (): void => {
      isOpen = false;
      clearTimeout(timeoutId);
    };
  }, [debouncedProviderIdSuffix]);

  if (!isLookedUp(providerIdSuffix)) {
    return { status: 'inconclusive' };
  }
  // An answer for an earlier keystroke says nothing about the ID typed since.
  if (lookup?.providerIdSuffix !== providerIdSuffix) {
    // The effect above starts the lookup as soon as the debounced ID reaches the typed one.
    return debouncedProviderIdSuffix === providerIdSuffix ? { status: 'checking' } : { status: 'debouncing' };
  }
  return lookup.configuredTypes
    ? { status: 'answered', configuredTypes: lookup.configuredTypes }
    : { status: 'inconclusive' };
};
